import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const LAST_UPDATED = '18 May 2026';

export default function PrivacyPage() {
  return (
    <div className="privacy-page" style={{ maxWidth: 880, margin: '0 auto', padding: '32px 20px 80px' }}>
      <div style={{ marginBottom: 24 }}>
        <Link to="/" className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ArrowLeft size={16} /> Back
        </Link>
      </div>

      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <Shield size={28} />
        <h1 style={{ margin: 0 }}>Privacy Policy</h1>
      </header>
      <p style={{ color: 'var(--muted, #6b7280)', marginTop: 0, marginBottom: 32, fontSize: 14 }}>
        UniHub (hub.nexkoins.com) · Last updated: {LAST_UPDATED}
      </p>

      <div style={{ lineHeight: 1.65, fontSize: 15 }}>

        <section style={{ marginBottom: 32 }}>
          <p>
            UniHub (hub.nexkoins.com) is the internal business operations platform for NexKoins
            and its portfolio entities (KNG, KYC, R&amp;R, NS, NK). This is a private platform for
            authorized staff and partners only &mdash; not a public service.
          </p>
          <p>
            Questions? Contact:{' '}
            <a href="mailto:exec.ai@outlook.com">exec.ai@outlook.com</a>
          </p>
        </section>

        <h2>1. Who this policy applies to</h2>
        <p>This policy applies to:</p>
        <ul>
          <li>NexKoins staff and trainers who use UniHub for daily operations</li>
          <li>Authorized partners with delegated business access</li>
          <li>Service providers acting on our behalf</li>
        </ul>
        <p>This policy does NOT apply to:</p>
        <ul>
          <li>Members or customers of KNG, KYC, R&amp;R &mdash; see their respective entity policies</li>
          <li>Visitors to public-facing marketing websites</li>
        </ul>

        <h2>2. What we collect</h2>
        <p>To grant you access to UniHub, we collect:</p>
        <ul>
          <li><strong>Identifying info:</strong> name, role, business assignment</li>
          <li><strong>Authentication info:</strong> email address, password (hashed, never stored in plain text), MFA seed if enabled</li>
          <li><strong>Activity info:</strong> login times, IP addresses, actions performed (audit trail)</li>
          <li><strong>Business data:</strong> payroll records, timesheets, expense receipts, member info you input on behalf of the business</li>
        </ul>

        <h2>3. Why we collect it</h2>
        <ul>
          <li>Grant and manage your access (performance of employment / partnership)</li>
          <li>Audit trail for compliance and security (legal obligation + legitimate interest)</li>
          <li>Pay you, if you are a trainer or staff member (performance of contract)</li>
          <li>Detect and respond to security incidents (legitimate interest)</li>
        </ul>

        <h2>4. Access control</h2>
        <ul>
          <li>All access is invite-only. Self-registration is disabled.</li>
          <li>Access is granted per business; you cannot see businesses you weren&apos;t invited to.</li>
          <li>Sensitive actions (payroll, deletion) are logged.</li>
          <li>Inactive accounts (no login for 90 days) are reviewed and may be disabled.</li>
        </ul>

        <h2>5. How long we keep it</h2>
        <ul>
          <li><strong>Active account records:</strong> while your employment/partnership is active</li>
          <li><strong>Inactive account records:</strong> disabled within 90 days of departure; metadata retained 7 years for audit</li>
          <li><strong>Audit logs:</strong> 1 year minimum, 7 years for financial actions</li>
          <li><strong>Business data (payroll, timesheets):</strong> 7 years (Singapore Employment Act + IRAS)</li>
        </ul>

        <h2>6. Your rights</h2>
        <p>As a staff member or partner, you can:</p>
        <ul>
          <li><strong>Access</strong> the personal data we hold about you</li>
          <li><strong>Correct</strong> inaccurate data</li>
          <li><strong>Request deletion</strong> when your engagement ends (subject to legal retention)</li>
          <li><strong>Receive a copy</strong> of your records on departure</li>
        </ul>
        <p>
          Email <a href="mailto:exec.ai@outlook.com">exec.ai@outlook.com</a> &mdash;
          we respond within 30 days.
        </p>

        <h2>7. Security</h2>
        <ul>
          <li>All connections use HTTPS/TLS</li>
          <li>Authentication backed by Google Firebase Identity Platform</li>
          <li>Multi-factor authentication recommended for all admin accounts</li>
          <li>Role-based access control on every data resource</li>
          <li>Regular security audits, secret rotations, and backup restoration drills</li>
        </ul>

        <h2>8. What you must do</h2>
        <p>As an authorized user, you agree to:</p>
        <ul>
          <li>Keep your password confidential</li>
          <li>Enable MFA when prompted</li>
          <li>Not share your account</li>
          <li>Report any suspected compromise immediately to <a href="mailto:exec.ai@outlook.com">exec.ai@outlook.com</a></li>
          <li>Use UniHub only for authorized business purposes</li>
          <li>Comply with PDPA when handling member data through the platform</li>
        </ul>

        <h2>9. Subprocessors</h2>
        <ul>
          <li><strong>Google Firebase / Google Cloud</strong> &mdash; authentication and database (Singapore region)</li>
          <li><strong>Cloudflare</strong> &mdash; DNS, TLS, WAF</li>
          <li><strong>Hostinger</strong> &mdash; server infrastructure</li>
          <li><strong>Netlify</strong> &mdash; web hosting</li>
          <li><strong>Anthropic</strong> &mdash; AI processing (for internal operations support)</li>
        </ul>
        <p>All bound by their own data protection terms.</p>

        <h2>10. Changes</h2>
        <p>Material changes are notified by email to active users.</p>

        <h2>11. Contact</h2>
        <p>
          NexKoins<br />
          Singapore<br />
          Email: <a href="mailto:exec.ai@outlook.com">exec.ai@outlook.com</a> (DPO)
        </p>
        <p>
          PDPA complaints:{' '}
          <a href="https://pdpc.gov.sg" target="_blank" rel="noopener noreferrer">pdpc.gov.sg</a>
        </p>
      </div>
    </div>
  );
}
